import React, {
  useState,
  useContext,
  useEffect
} from 'react';
import { Col, Row } from '@thd-olt-component-react/grid';
import { TitleTag, useHelmet } from '@thd-nucleus/thd-helmet';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useParams, useLocation } from '@thd-olt-component-react/router';
import { ArticleInfo } from '@thd-olt-component-react/article-info';
import { ToolsMaterialsWrapper } from '@thd-olt-component-react/tools-materials';
import { RelatedArticles } from '@thd-olt-component-react/related-articles';
import { ArticleSections, Dropdown } from '@thd-olt-component-react/article-section';
import { ArticleGenLead } from '@thd-olt-component-react/home-services';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { AuthorTitle } from '@thd-olt-component-react/author-pod';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { ArticleMetadata, Metadata } from '@thd-olt-component-react/metadata';
import { SearchFeedback } from '@thd-olt-component-react/search-feedback';
import {
  extend,
  params,
  shape,
  arrayOf,
  string as stringType,
  useDataModel,
  QueryContext,
} from '@thd-nucleus/data-sources';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import {
  Typography,
  useBreakpoint,
} from '@one-thd/sui-atomic-components';
import classNames from 'classnames';

import { imagePreloader } from '../utils';

export const Article = () => {
  const homeServicesLogo = 'https://assets.homedepot-static.com/images/v1/home-services-icon.svg';
  declareContexts([ExperienceContext, QueryContext]);
  const { articleGUID } = useParams();

  const lg = useBreakpoint('lg');
  const [isLgPlus, setIsLgPlus] = useState(false);

  useEffect(() => {
    setIsLgPlus(lg.greater);
  }, [lg]);

  const { data, loading, error } = useDataModel('article', {
    variables: {
      articleGUID,
    },
  });
  const functionToCache = ({ data }) => {
  return [
    new TitleTag('String to Title'),
    new MetaTag({ property: 'og:title', content: 'How to Become an OG Expert (8 Steps)' }),
  ];
};

  useHelmet('article', { data }, imagePreloader, [data], functionToCache, [data]);

  let breadCrumbs = data?.article?.breadcrumbs || [];
  const path = useLocation().pathname;

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (data && !loading) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('article-experience.page-load', {
          articleGUID,
          breadCrumbs,
        });
      }
    }
  }, []);

  if (!data || loading || error) {
    return null;
  }

  const sections = data?.article?.sections || [];
  const isInfluencer = data?.article?.articleType === 'ap';

  return (
    <div
      className={
        classNames(
          'sui-flex sui-flex-col sui-bg-primary sui-gap-6 sui-px-4 sui-py-4',
          'md:sui-px-6',
          'lg:sui-px-8',
          'xl:sui-px-auto',
        )
      }
    >
      <div className="sui-flex sui-flex-col sui-w-full sui-max-w-[1376px] xl:sui-self-center">
        <ErrorBoundary name="metadata">
          <Metadata>
            <ArticleMetadata articleGUID={articleGUID} />
          </Metadata>
        </ErrorBoundary>

        <ErrorBoundary name="breadcrumbs">
          <Breadcrumbs staticCrumbs={breadCrumbs} />
        </ErrorBoundary>
      </div>

      <div className="sui-flex sui-flex-col sui-gap-2 sui-items-center">
        <div className="sui-grid sui-grid-cols-1 lg:sui-grid-cols-12 lg:sui-grid-rows-1 sui-gap-4">
          {
            isLgPlus && (
              <>
                <div className="sui-col-span-3 sui-max-w-[332px]">
                  <div className="sui-flex sui-flex-col sui-gap-8">
                    <div>
                      <ErrorBoundary name="article-sections-table-of-contents">
                        <Dropdown
                          sections={sections}
                        />
                      </ErrorBoundary>
                    </div>

                    <div className="related-container">
                      <ErrorBoundary name="tools-materials">
                        <ToolsMaterialsWrapper articleGUID={articleGUID} />
                      </ErrorBoundary>
                    </div>

                    <div className="related-container">
                      <ErrorBoundary name="related-articles">
                        <RelatedArticles articleGUID={articleGUID} />
                      </ErrorBoundary>
                    </div>
                    <div className="related-container">
                      {
                        data?.article?.Widget
                        && (
                          <img
                            src={homeServicesLogo}
                            className="sui-m-0"
                            alt="Home Services Logo, Leave Your Next Project to Us"
                            height="auto"
                            width="100"
                            loading="lazy"
                          />
                        )
                      }
                      <ErrorBoundary name="gen-lead">
                        <div
                          className="[&_p:first-child]:sui-hidden"
                        >
                          <ArticleGenLead articleGUID={articleGUID} />
                        </div>
                      </ErrorBoundary>
                    </div>
                  </div>
                </div>

                <div className="sui-col-span-1 sui-max-w-[100px]" />
              </>
            )
          }

          <div className="lg:sui-col-span-8 lg:sui-col-start-5 sui-max-w-[912px]">
            <ErrorBoundary name="author-title">
              <div
                className="sui-mb-3"
              >
                <AuthorTitle articleGUID={articleGUID} />
              </div>
            </ErrorBoundary>

            <div className="sui-mb-4">
              <ErrorBoundary name="article-info">
                <ArticleInfo articleGUID={articleGUID} />
              </ErrorBoundary>
            </div>

            {
              !isLgPlus && (
                <ErrorBoundary name="article-sections-table-of-contents">
                  <Dropdown
                    sections={sections}
                  />
                </ErrorBoundary>
              )
            }

            <Row className="article-info-sections__section">
              <ErrorBoundary name="article-sections">
                <ArticleSections articleGUID={articleGUID} />
              </ErrorBoundary>
              {isInfluencer && (
                <ErrorBoundary name="article-legal-disclaimer">
                  <hr className="sui-w-full sui-my-6 sui-divide-dashed" />
                  <Typography
                    variant="body-base"
                  >
                    This post was created in partnership with The Home Depot. The author may have
                    received compensation for this article and associated images or video, potentially
                    in the form of products and/or services. All expressed opinions and experiences
                    are the author’s own words.
                  </Typography>
                </ErrorBoundary>
              )}
            </Row>

            {
              !isLgPlus && (
                <div className="sui-flex sui-flex-col sui-gap-4">
                  <ErrorBoundary name="tools-materials">
                    <ToolsMaterialsWrapper articleGUID={articleGUID} />
                  </ErrorBoundary>

                  <ErrorBoundary name="related-articles">
                    <RelatedArticles articleGUID={articleGUID} />
                  </ErrorBoundary>

                  <div>
                    {
                      data?.article?.Widget
                      && (
                        <img
                          src={homeServicesLogo}
                          className="sui-m-0"
                          alt="Home Services Logo, Leave Your Next Project to Us"
                          height="auto"
                          width="100"
                          loading="lazy"
                        />
                      )
                    }
                    <ErrorBoundary name="gen-lead">
                      <div
                        className="[&_p:first-child]:sui-hidden"
                      >
                        <ArticleGenLead articleGUID={articleGUID} />
                      </div>
                    </ErrorBoundary>
                  </div>

                </div>
              )
            }
          </div>
        </div>

        <ErrorBoundary
          name="article-feedback"
        >
          <Col>
            <SearchFeedback type="articles" />
          </Col>
        </ErrorBoundary>
        <Row className="isBound emtLinks">
          <EmtLinks path={path} />
        </Row>
      </div>
    </div>
  );
};

Article.propTypes = {};

Article.dataModel = extend(
  {
    article: params({
      articleGUID: stringType().isRequired(),
    }).shape({
      breadcrumbs: arrayOf(
        shape({
          dimensionName: stringType(),
          url: stringType(),
          label: stringType(),
        })
      ),
      title: stringType(),
      metadata: shape({
        description: stringType(),
        siteName: stringType(),
        title: stringType(),
        type: stringType(),
        heading: stringType(),
        canonicalURL: stringType(),
        facebookAdmins: stringType(),
      }),
      schemaTag: stringType(),
    }),
  },
  Breadcrumbs,
  AuthorTitle,
  ArticleGenLead,
  ArticleInfo,
  ToolsMaterialsWrapper,
  ArticleSections,
  RelatedArticles,
  EmtLinks,
  ArticleMetadata
);

Article.displayName = 'Article';

Article.defaultProps = {};
